import { escapeIncompatibleMupPattern } from '@merchant-portal/mp-models'
import {
  isExistingPartPayment,
  PaymentMethods,
} from '../models/payment-methods'
import {
  PredefinedFormFields,
  PredefinedHeadings,
} from '../pages/PayApply/pages/ApplicationFormPage/predefinedFormFields'

import { format } from '../models/format'
import { getText } from '../internal/TextLabels'
import { CustomerTypeModel } from '../pages/PayApply/models/private-models'
import { CustomerHelper } from './customerHelper'
import { getDeepOr } from './helpers'

export const validateGovernmentId = (fieldValue, expr) => {
  const regex = new RegExp(expr)
  const tenDigits = fieldValue.slice(2, 12)
  const tenDigitsDash = `${tenDigits.slice(0, 6)}-${fieldValue.slice(8)}`
  const twelveDigitsDash = `${fieldValue.slice(0, 8)}-${fieldValue.slice(8)}`
  if (regex.test(fieldValue)) {
    return fieldValue
  }
  if (regex.test(tenDigits)) {
    return tenDigits
  }
  if (regex.test(tenDigitsDash)) {
    return tenDigitsDash
  }
  if (regex.test(twelveDigitsDash)) {
    return twelveDigitsDash
  }
  return ''
}

const shouldHaveCoApplicant = ({
  selectedPaymentMethod: { type: paymentMethodType },
  coApplicant,
}) => {
  if (
    [
      PaymentMethods.RESURS_REVOLVING_CREDIT,
      PaymentMethods.RESURS_CARD,
    ].includes(paymentMethodType)
  ) {
    return coApplicant
  }

  return true
}

export const initFormValues = (formData, state) => {
  let emailAdded = false
  let mobileAdded = false
  let coApplicant = false
  const formValues = formData ? JSON.parse(JSON.stringify(formData)) : []
  const customerHelper = CustomerHelper.fromPayApply(state)
  const coCustomerHelper = new CustomerHelper(state.current.coApplicant)

  for (let i = 0; i < formValues.length; i++) {
    const formField = formValues[i]

    switch (formField.name) {
      case PredefinedFormFields.APPLICANT_GOVERNMENT_ID:
        formField.value = validateGovernmentId(
          customerHelper.governmentId || '',
          formField.format,
        )
        if (formField.value) {
          formField.readOnly = true
          formField.disabled = true
          formField.hidden = true
        }
        break
      case PredefinedFormFields.APPLICANT_TELEPHONE_NUMBER:
        formField.value =
          customerHelper.homePhoneNumber || customerHelper.aPhoneNumber || ''
        if (formField.value) {
          formField.mandatory = false
          formField.hidden = true
        }
        break
      case PredefinedFormFields.APPLICANT_MOBILE_NUMBER:
        formField.value = customerHelper.mobilePhoneNumber || ''
        formField.format =
          format.mobilePhone.regex &&
          escapeIncompatibleMupPattern(format.mobilePhone.regex)
        mobileAdded = true
        break
      case PredefinedFormFields.APPLICANT_EMAIL_ADDRESS:
        formField.label = getText('payApply.paymentMethodForm.eMail')
        formField.value = customerHelper.email || ''
        emailAdded = true
        break
      case PredefinedFormFields.AMOUNT:
        formField.value = state.current.selectedLimit
        formField.type = 'number'
        formField.readOnly = true
        formField.disabled = true
        formField.mandatory = false
        formField.hidden = true
        break
      case PredefinedFormFields.CARD_NUMBER:
        formField.value = state.current.selectedCard
        formField.hidden = true
        break
      case PredefinedFormFields.CO_APPLICANT_ID:
        coApplicant = shouldHaveCoApplicant(state.current)
        if (
          state.current.coApplicant &&
          state.current.coApplicant.governmentId
        ) {
          formField.value = state.current.coApplicant.governmentId
          formField.readOnly = true
          formField.disabled = true
          formField.mandatory = true
        }

        break
      case PredefinedFormFields.CO_APPLICANT_PHONE:
        formField.value = coCustomerHelper.mobilePhoneNumber || ''
        if (formField.value) {
          formField.mandatory = true
        }
        break
      case PredefinedFormFields.CO_APPLICANT_EMAIL:
        formField.value = coCustomerHelper.email || ''
        if (formField.value) {
          formField.mandatory = true
        }
        break
      case PredefinedFormFields.INCREASE_EXISTING_CERDIT:
        formField.value = true
        break
      case PredefinedFormFields.ESKAT_CONSENT:
        if (formField.type === 'hidden') {
          formField.value = true
        }
        break
      default:
        break
    }
  }

  const legalCustomer = state.wizard.wizard === CustomerTypeModel.LEGAL
  if (!legalCustomer && !mobileAdded) {
    formValues.push({
      name: PredefinedFormFields.APPLICANT_MOBILE_NUMBER,
      value: customerHelper.mobilePhoneNumber,
      label: getText('payApply.paymentMethodForm.mobileNumber'),
      type: 'text',
      mandatory: true,
      format: format.mobilePhone.regex,
      formatMessage: format.mobilePhone.error,
    })
  }

  if (!legalCustomer && !emailAdded) {
    formValues.push({
      name: PredefinedFormFields.APPLICANT_EMAIL_ADDRESS,
      value: customerHelper.email,
      format: format.email.regex,
      formatMessage: format.email.error,
      label: getText('payApply.paymentMethodForm.eMail'),
      type: 'text',
      mandatory: true,
    })
  }

  if (coApplicant) {
    const coApplicantCheckIndex = formValues.findIndex(({ name }) =>
      [
        PredefinedHeadings.COAPPLICANT_BLOCK,
        PredefinedHeadings.SUBTITLE_COAPPLICANT,
        PredefinedFormFields.CO_APPLICANT_ID,
      ].includes(name),
    )
    formValues.splice(
      coApplicantCheckIndex,
      0,
      { type: 'br', label: '' },
      {
        name: 'co-applicant-check',
        value:
          (state.current.selectedCard &&
            state.current.selectedCard.coApplicant !== null) ||
          (state.current.coApplicant &&
            state.current.coApplicant.governmentId !== null),
        label: getText('payApply.paymentMethodForm.coApplicant'),
        type: 'bold-switch',
        disabled:
          state.current.coApplicant &&
          state.current.coApplicant.governmentId !== null,
      },
    )
  }
  return formValues
}

export const getPaymentMethodDescription = paymentMethod =>
  isExistingPartPayment(paymentMethod)
    ? getDeepOr(
        paymentMethod.description,
        paymentMethod,
        'accounts.0.bankProductName',
      )
    : paymentMethod.description || paymentMethod.paymentMethodDescription
